import {
  BROWSER_RESIZE_JPEG_QUALITY_STEPS,
  BROWSER_RESIZE_MAX_BYTES,
  BROWSER_RESIZE_MAX_EDGE_PX,
  IMAGES_INPUT_MAX_BYTES,
  IMAGES_INPUT_MAX_EDGE_PX,
} from "@ckgo2/shared";
import type { BatchPermanentReason, BrowserCaps, PrepDecision } from "./batchUpload.types";
import { uploadTypeOf } from "./fileTypes";

/**
 * Příprava souboru dávky v prohlížeči (vize podnety-tymu-2026-10, F7.8): soubor nad 6000 px
 * delší strany nebo nad 20 MB se zmenší na 6000 px delší strany do JPEG ve vysoké kvalitě
 * (PNG s průhledností a GIF se nezmenšují), TIF a HEIC podle schopnosti prohlížeče.
 *
 * Do JPEG, ne WebP: Safari WebP v prohlížeči kódovat neumí (vize R11). Plátno kreslí do sRGB
 * a JPEG bez profilu je sRGB. Hlavní verzi a čtverec dál dělá server z poslaného souboru.
 */

export type FileKind = "jpeg" | "png" | "gif" | "webp" | "tif" | "heic" | "other";

const KIND_BY_TYPE: Readonly<Record<string, FileKind>> = {
  "image/jpeg": "jpeg",
  "image/png": "png",
  "image/gif": "gif",
  "image/webp": "webp",
  "image/tiff": "tif",
  "image/heic": "heic",
  "image/heif": "heic",
};

/** Druh souboru podle typu z `uploadTypeOf` (prázdný typ → podle přípony). */
export function fileKind(f: { type: string; name: string }): FileKind {
  const type = uploadTypeOf(f);
  return Object.prototype.hasOwnProperty.call(KIND_BY_TYPE, type) ? KIND_BY_TYPE[type] : "other";
}

const RESIZE: PrepDecision = { kind: "resize", maxEdge: BROWSER_RESIZE_MAX_EDGE_PX };
const AS_IS: PrepDecision = { kind: "as-is" };

/**
 * Rozhodnutí o zmenšení souboru v prohlížeči (F7.8). Prahy jsou ostré: soubor přesně 6000 px
 * nebo přesně `BROWSER_RESIZE_MAX_BYTES` odejde beze změny. Bez rozměrů rozhoduje jen
 * velikost; `hasAlpha` neznámé u PNG = bez průhlednosti. `maxEdge` je strop, ne cíl: menší
 * obrázek (převáděný TIF) se nezvětšuje.
 */
export function decideBrowserPrep(
  f: {
    type: string;
    name: string;
    size: number;
    width?: number;
    height?: number;
    hasAlpha?: boolean;
  },
  caps: BrowserCaps,
): PrepDecision {
  const kind = fileKind(f);
  const overBytes = f.size > BROWSER_RESIZE_MAX_BYTES;
  const overEdge =
    !!f.width && !!f.height && Math.max(f.width, f.height) > BROWSER_RESIZE_MAX_EDGE_PX;
  const over = overBytes || overEdge;
  switch (kind) {
    case "gif":
    case "other":
      return AS_IS;
    case "png":
      if (f.hasAlpha === true) return AS_IS;
      return over ? RESIZE : AS_IS;
    case "tif":
      // Images binding TIF nepřijme (vize §6): bez dekodéru trvalá chyba, s ním převod vždy.
      return caps.decodesTif ? RESIZE : { kind: "permanent", reason: "tif-unsupported" };
    case "heic":
      if (caps.decodesHeic) return over ? RESIZE : AS_IS;
      // Bez dekodéru HEIC dekóduje server, ale jen do svého limitu bajtů.
      return overBytes ? { kind: "permanent", reason: "heic-too-large" } : AS_IS;
    case "jpeg":
    case "webp":
      return over ? RESIZE : AS_IS;
  }
}

// ── hlavička souboru ──────────────────────────────────────────────────────────

export interface ImageHeader {
  width: number;
  height: number;
  hasAlpha: boolean;
}

type SliceableBlob = Pick<Blob, "size" | "slice">;

/** Nejvýš tolik bajtů načte jeden výřez; segmenty APP (EXIF, ICC) bývají desítky kB. */
const HEADER_WINDOW_BYTES = 64 * 1024;
/** Nejvýš tolik segmentů JPEG nebo chunků PNG se projde, než čtení vzdá. */
const HEADER_MAX_SEGMENTS = 64;

/**
 * Čtení po výřezech: okno se načte znovu jen tehdy, když požadované bajty leží mimo něj.
 * Mimo rozsah souboru vrací `null`.
 */
function sliceReader(blob: SliceableBlob) {
  let winStart = 0;
  let win = new Uint8Array(0);
  return async function bytes(offset: number, length: number): Promise<Uint8Array | null> {
    if (offset < 0 || offset + length > blob.size) return null;
    if (offset < winStart || offset + length > winStart + win.length) {
      const end = Math.min(blob.size, offset + Math.max(length, HEADER_WINDOW_BYTES));
      win = new Uint8Array(await blob.slice(offset, end).arrayBuffer());
      winStart = offset;
      if (win.length < length) return null;
    }
    return win.subarray(offset - winStart, offset - winStart + length);
  };
}

type ByteReader = ReturnType<typeof sliceReader>;

const be16 = (b: Uint8Array, o: number) => (b[o] << 8) | b[o + 1];
const be32 = (b: Uint8Array, o: number) =>
  ((b[o] << 24) | (b[o + 1] << 16) | (b[o + 2] << 8) | b[o + 3]) >>> 0;
const le16 = (b: Uint8Array, o: number) => b[o] | (b[o + 1] << 8);
const le24 = (b: Uint8Array, o: number) => b[o] | (b[o + 1] << 8) | (b[o + 2] << 16);
const le32 = (b: Uint8Array, o: number) =>
  (b[o] | (b[o + 1] << 8) | (b[o + 2] << 16) | (b[o + 3] << 24)) >>> 0;
const fourcc = (b: Uint8Array, o: number) =>
  String.fromCharCode(b[o], b[o + 1], b[o + 2], b[o + 3]);

/** SOF markery s rozměry (ne DHT `C4`, JPG `C8`, DAC `CC`). */
function isSofMarker(m: number): boolean {
  return m >= 0xc0 && m <= 0xcf && m !== 0xc4 && m !== 0xc8 && m !== 0xcc;
}

async function readJpegHeader(read: ByteReader): Promise<ImageHeader | null> {
  let pos = 2;
  for (let i = 0; i < HEADER_MAX_SEGMENTS; i++) {
    const head = await read(pos, 4);
    if (!head || head[0] !== 0xff) return null;
    const marker = head[1];
    if (marker === 0xff) {
      pos += 1; // výplňový bajt před markerem
      continue;
    }
    if (marker === 0x01 || (marker >= 0xd0 && marker <= 0xd7)) {
      pos += 2; // samostatné markery bez délky
      continue;
    }
    if (marker === 0xda || marker === 0xd9) return null; // data obrazu bez SOF
    const length = be16(head, 2);
    if (length < 2) return null;
    if (isSofMarker(marker)) {
      const sof = await read(pos + 4, 5);
      if (!sof) return null;
      const height = be16(sof, 1);
      const width = be16(sof, 3);
      return width > 0 && height > 0 ? { width, height, hasAlpha: false } : null;
    }
    pos += 2 + length;
  }
  return null;
}

async function readPngHeader(read: ByteReader): Promise<ImageHeader | null> {
  const ihdr = await read(8, 25);
  if (!ihdr || fourcc(ihdr, 4) !== "IHDR") return null;
  const width = be32(ihdr, 8);
  const height = be32(ihdr, 12);
  const colorType = ihdr[17];
  if (width === 0 || height === 0) return null;
  if (colorType === 4 || colorType === 6) return { width, height, hasAlpha: true };
  let pos = 8 + 12 + be32(ihdr, 0);
  for (let i = 0; i < HEADER_MAX_SEGMENTS; i++) {
    const chunk = await read(pos, 8);
    if (!chunk) break;
    const type = fourcc(chunk, 4);
    if (type === "tRNS") return { width, height, hasAlpha: true };
    if (type === "IDAT" || type === "IEND") break;
    pos += 12 + be32(chunk, 0);
  }
  return { width, height, hasAlpha: false };
}

async function readWebpHeader(read: ByteReader): Promise<ImageHeader | null> {
  const b = await read(0, 30);
  if (!b || fourcc(b, 8) !== "WEBP") return null;
  const chunk = fourcc(b, 12);
  let width = 0;
  let height = 0;
  if (chunk === "VP8X") {
    width = le24(b, 24) + 1;
    height = le24(b, 27) + 1;
  } else if (chunk === "VP8 ") {
    width = le16(b, 26) & 0x3fff;
    height = le16(b, 28) & 0x3fff;
  } else if (chunk === "VP8L") {
    if (b[20] !== 0x2f) return null;
    const v = le32(b, 21);
    width = (v & 0x3fff) + 1;
    height = ((v >> 14) & 0x3fff) + 1;
  } else {
    return null;
  }
  // Průhlednost se rozhoduje jen u PNG (výjimka F7.8); u WebP se nezjišťuje.
  return width > 0 && height > 0 ? { width, height, hasAlpha: false } : null;
}

/**
 * Rozměry (a u PNG průhlednost) z hlavičky JPEG, PNG nebo WebP. Čte jen výřezy, nikdy celý
 * soubor (dávka 20 × 12 MB by jinak dekódovala 240 MB jen kvůli rozměrům). Orientace z EXIF
 * delší stranu nemění, rozměry se proto nepřehazují. Jiný druh nebo poškozená hlavička → `null`.
 */
export async function readImageHeader(blob: SliceableBlob): Promise<ImageHeader | null> {
  try {
    const read = sliceReader(blob);
    const sig = await read(0, 12);
    if (!sig) return null;
    if (sig[0] === 0xff && sig[1] === 0xd8) return await readJpegHeader(read);
    if (sig[0] === 0x89 && fourcc(sig, 1) === "PNG\r") return await readPngHeader(read);
    if (fourcc(sig, 0) === "RIFF" && fourcc(sig, 8) === "WEBP") return await readWebpHeader(read);
    return null;
  } catch {
    return null;
  }
}

// ── dekódování, plátno, kódování ──────────────────────────────────────────────

export interface DecodedImage {
  /** Rozměry po uplatnění orientace z EXIF. */
  width: number;
  height: number;
  source: CanvasImageSource;
  close(): void;
}

type Context2D = {
  fillStyle: string | CanvasGradient | CanvasPattern;
  imageSmoothingEnabled: boolean;
  imageSmoothingQuality: ImageSmoothingQuality;
  fillRect(x: number, y: number, w: number, h: number): void;
  drawImage(image: CanvasImageSource, dx: number, dy: number, dw: number, dh: number): void;
};

/** Plátno zmenšení: `OffscreenCanvas` nebo `<canvas>` (oba tento tvar splňují). */
export interface PrepCanvas {
  width: number;
  height: number;
  getContext(contextId: "2d"): Context2D | null;
  convertToBlob?(options?: ImageEncodeOptions): Promise<Blob>;
  toBlob?(callback: BlobCallback, type?: string, quality?: number): void;
}

export interface PrepDeps {
  decodeImage(blob: Blob): Promise<DecodedImage>;
  createCanvas(width: number, height: number): PrepCanvas;
  encodeJpeg(canvas: PrepCanvas, quality: number): Promise<Blob>;
}

type CreateBitmap = (image: Blob, options?: ImageBitmapOptions) => Promise<ImageBitmap>;

/**
 * Bitmapa s orientací z EXIF. Prohlížeč, který hodnotu `from-image` nezná, ji odmítne
 * `TypeError`; druhý pokus bez voleb (výchozí chování dnešních prohlížečů orientaci uplatní).
 */
export async function createOrientedBitmap(blob: Blob, create: CreateBitmap): Promise<ImageBitmap> {
  try {
    return await create(blob, { imageOrientation: "from-image" });
  } catch (err) {
    if (err instanceof TypeError) return await create(blob);
    throw err;
  }
}

async function decodeWithImageElement(blob: Blob): Promise<DecodedImage> {
  if (typeof document === "undefined") throw new Error("browserPrep: chybí document");
  const url = URL.createObjectURL(blob);
  const img = document.createElement("img");
  try {
    img.src = url;
    await img.decode();
  } catch (err) {
    URL.revokeObjectURL(url);
    throw err;
  }
  return {
    width: img.naturalWidth,
    height: img.naturalHeight,
    source: img,
    close: () => {
      URL.revokeObjectURL(url);
      img.removeAttribute("src");
    },
  };
}

/**
 * Dekódování s orientací z EXIF: `createImageBitmap`, a když chybí nebo selže, `<img>` nad
 * `URL.createObjectURL` (`naturalWidth`/`naturalHeight` jsou po orientaci).
 */
export async function decodeImage(blob: Blob): Promise<DecodedImage> {
  if (typeof createImageBitmap === "function") {
    try {
      const bitmap = await createOrientedBitmap(blob, createImageBitmap);
      return {
        width: bitmap.width,
        height: bitmap.height,
        source: bitmap,
        close: () => bitmap.close(),
      };
    } catch {
      // záložní cesta přes <img> níže
    }
  }
  return decodeWithImageElement(blob);
}

function createCanvas(width: number, height: number): PrepCanvas {
  if (typeof OffscreenCanvas === "function") return new OffscreenCanvas(width, height);
  const canvas = document.createElement("canvas");
  canvas.width = width;
  canvas.height = height;
  return canvas;
}

/** JPEG z plátna; výsledek jiného typu (prohlížeč JPEG nezakóduje) je chyba. */
export async function encodeJpeg(canvas: PrepCanvas, quality: number): Promise<Blob> {
  let blob: Blob | null;
  if (canvas.convertToBlob) {
    blob = await canvas.convertToBlob({ type: "image/jpeg", quality });
  } else if (canvas.toBlob) {
    const toBlob = canvas.toBlob.bind(canvas);
    blob = await new Promise<Blob | null>((resolve) => toBlob(resolve, "image/jpeg", quality));
  } else {
    blob = null;
  }
  if (!blob || blob.type !== "image/jpeg") throw new Error("browserPrep: JPEG se nezakódoval");
  return blob;
}

const DEFAULT_DEPS: PrepDeps = { decodeImage, createCanvas, encodeJpeg };

// ── příprava ──────────────────────────────────────────────────────────────────

export type PrepResult = { blob: Blob; resized: boolean } | { permanent: BatchPermanentReason };

/**
 * Zámek modulu: najednou dekóduje a kóduje jen jeden soubor (bitmapa 80MB JPEG má stovky MB,
 * čtyři souběžné by kartu shodily). Úloha `resizeLocked` sama nevyhazuje; kdyby přesto,
 * odmítnutý článek řetězu další volání nezablokuje.
 */
let chain: Promise<unknown> = Promise.resolve();
function underLock<T>(task: () => Promise<T>): Promise<T> {
  const run = chain.then(task);
  chain = run.catch(() => undefined);
  return run;
}

/** Cílové rozměry: delší strana přesně `maxEdge` při zmenšení, menší obrázek se nezvětšuje. */
function targetSize(width: number, height: number, maxEdge: number) {
  const longer = Math.max(width, height);
  if (longer <= maxEdge) return { width, height };
  const scale = maxEdge / longer;
  return width >= height
    ? { width: maxEdge, height: Math.max(1, Math.round(height * scale)) }
    : { width: Math.max(1, Math.round(width * scale)), height: maxEdge };
}

/**
 * Pojistka kroku pod zámkem (dekódování, jeden stupeň kódování): poškozený TIF nebo HEIC
 * v Safari umí `createImageBitmap` nechat viset navždy a zámek by pak držel celou kartu.
 * Velkorysá, protože dekódování 80MB JPEG na slabém stroji trvá jednotky sekund.
 */
export const PREP_STEP_TIMEOUT_MS = 60_000;

/**
 * Doběhne jako `work`, nebo vyhodí po `PREP_STEP_TIMEOUT_MS` či zrušením dávky (`signal`);
 * výsledek, který dorazí pozdě, předá `onLate` (bitmapu je třeba zavřít).
 */
function withinDeadline<T>(
  work: Promise<T>,
  signal: AbortSignal | undefined,
  onLate: (value: T) => void = () => {},
): Promise<T> {
  return new Promise<T>((resolve, reject) => {
    let settled = false;
    const stop = (error: Error) => {
      if (settled) return;
      settled = true;
      cleanup();
      reject(error);
    };
    const onAbort = () => stop(new Error("browserPrep: dávka zrušena"));
    const timer = setTimeout(
      () => stop(new Error("browserPrep: krok nedoběhl")),
      PREP_STEP_TIMEOUT_MS,
    );
    function cleanup() {
      clearTimeout(timer);
      signal?.removeEventListener("abort", onAbort);
    }
    signal?.addEventListener("abort", onAbort, { once: true });
    work.then(
      (value) => {
        if (settled) return quietly(() => onLate(value));
        settled = true;
        cleanup();
        resolve(value);
      },
      (error: unknown) => stop(error instanceof Error ? error : new Error(String(error))),
    );
  });
}

function quietly(fn: () => void): void {
  try {
    fn();
  } catch {
    // úklid paměti nesmí přebít výsledek
  }
}

/** Nakreslí bitmapu do plátna a zakóduje JPEG po stupních kvality; bitmapu vždy zavře. */
async function drawAndEncode(
  decoded: DecodedImage,
  maxEdge: number,
  deps: PrepDeps,
  signal: AbortSignal | undefined,
): Promise<PrepResult> {
  let canvas: PrepCanvas | null = null;
  try {
    const size = targetSize(decoded.width, decoded.height, maxEdge);
    canvas = deps.createCanvas(size.width, size.height);
    const ctx = canvas.getContext("2d");
    if (!ctx) throw new Error("browserPrep: plátno bez kontextu 2d");
    ctx.imageSmoothingEnabled = true;
    ctx.imageSmoothingQuality = "high";
    // Průhledná místa (WebP, PNG bez zjištěné průhlednosti) by v JPEG zčernala.
    ctx.fillStyle = "#ffffff";
    ctx.fillRect(0, 0, size.width, size.height);
    ctx.drawImage(decoded.source, 0, 0, size.width, size.height);
    for (const quality of BROWSER_RESIZE_JPEG_QUALITY_STEPS) {
      const blob = await withinDeadline(deps.encodeJpeg(canvas, quality), signal);
      if (blob.size <= BROWSER_RESIZE_MAX_BYTES) return { blob, resized: true };
    }
    return { permanent: "server-limits" };
  } finally {
    quietly(() => decoded.close());
    const c = canvas;
    if (c) {
      // Safari drží paměť plátna, dokud ho nikdo nezmenší.
      quietly(() => {
        c.width = 0;
        c.height = 0;
      });
    }
  }
}

/**
 * Když se zmenšení nepovede: soubor, který by server přijal, odejde beze změny a rozhodne
 * server; jinak trvalá chyba (opakování by nepomohlo). Soubor nad limity serveru je
 * `server-limits`; TIF v limitech server nepřijme jako formát, takže `tif-unsupported`
 * (dekodér prohlížeče přečetl jen vzorek, ne tento TIF: LZW, 16 bitů, CMYK, vrstvy).
 */
function fallbackAfterFailure(file: File, kind: FileKind, longerEdge: number | null): PrepResult {
  const withinLimits =
    file.size <= IMAGES_INPUT_MAX_BYTES &&
    (longerEdge === null || longerEdge <= IMAGES_INPUT_MAX_EDGE_PX);
  if (!withinLimits) return { permanent: "server-limits" };
  return kind === "tif" ? { permanent: "tif-unsupported" } : { blob: file, resized: false };
}

/** Soubor pod zámkem; `knownEdge` = delší strana z hlavičky (záloha po selhání). */
interface LockedSubject {
  file: File;
  kind: FileKind;
  knownEdge: number | null;
}

/**
 * Pod zámkem dekóduje, rozhodne a zmenší; při selhání nebo nedoběhnutém kroku
 * (`PREP_STEP_TIMEOUT_MS`) záloha. `decide` dostane dekódovaný obrázek; jiné rozhodnutí než
 * `resize` obrázek zavře a vrátí výsledek bez kódování. Zrušená dávka (`signal`) nedekóduje,
 * běžící krok opustí a vrátí soubor beze změny; volající takový výsledek zahodí.
 */
function resizeLocked(
  { file, kind, knownEdge }: LockedSubject,
  decide: (decoded: DecodedImage) => PrepDecision,
  deps: PrepDeps,
  signal: AbortSignal | undefined,
): Promise<PrepResult> {
  const unchanged: PrepResult = { blob: file, resized: false };
  return underLock(async () => {
    if (signal?.aborted) return unchanged;
    let decoded: DecodedImage;
    try {
      decoded = await withinDeadline(deps.decodeImage(file), signal, (late) => late.close());
    } catch {
      return signal?.aborted ? unchanged : fallbackAfterFailure(file, kind, knownEdge);
    }
    const decision = decide(decoded);
    if (decision.kind !== "resize") {
      quietly(() => decoded.close());
      return decision.kind === "permanent" ? { permanent: decision.reason } : unchanged;
    }
    try {
      return await drawAndEncode(decoded, decision.maxEdge, deps, signal);
    } catch {
      if (signal?.aborted) return unchanged;
      return fallbackAfterFailure(file, kind, knownEdge ?? Math.max(decoded.width, decoded.height));
    }
  });
}

/**
 * `prepareForUpload` s vloženým dekódováním, plátnem a kódováním (test). Nikdy nevyhazuje:
 * hlavička i zmenšení vlastní chyby polykají. `resized: true` znamená „bajty se změnily“
 * (i převod TIF bez zmenšení).
 *
 * Rozměry pocházejí z hlavičky, a když hlavička chybí, z dekódování. Bez dekódování se
 * rozhoduje hned (beze změny a trvalá chyba nečekají na zámek); jen druh, který hlavičku
 * nečteme a prohlížeč ho dekóduje (HEIC), rozhodne až nad dekódovaným obrázkem, dekóduje se
 * tedy jednou, pod zámkem.
 */
export async function prepareForUploadWith(
  file: File,
  caps: BrowserCaps,
  deps: PrepDeps = DEFAULT_DEPS,
  signal?: AbortSignal,
): Promise<PrepResult> {
  const kind = fileKind(file);
  const meta = { type: file.type, name: file.name, size: file.size };
  const header =
    kind === "jpeg" || kind === "png" || kind === "webp" ? await readImageHeader(file) : null;
  const decide = (dims: { width: number; height: number } | null) =>
    decideBrowserPrep({ ...meta, ...(header ?? dims) }, caps);
  const dimsFromDecode = header === null && kind === "heic" && caps.decodesHeic;
  const early = dimsFromDecode ? null : decide(null);
  if (early?.kind === "as-is") return { blob: file, resized: false };
  if (early?.kind === "permanent") return { permanent: early.reason };
  const knownEdge = header ? Math.max(header.width, header.height) : null;
  return resizeLocked({ file, kind, knownEdge }, (d) => early ?? decide(d), deps, signal);
}

/**
 * Připraví soubor k odeslání; nikdy nevyhazuje a dekódování serializuje uvnitř (souběžná
 * volání čekají na sebe). Jméno odesílaného souboru neurčuje: model dávky posílá
 * `formData.append("file", blob, file.name)`. `signal` = zrušená dávka (`BatchDeps.prepare`).
 */
export function prepareForUpload(
  file: File,
  caps: BrowserCaps,
  signal?: AbortSignal,
): Promise<PrepResult> {
  return prepareForUploadWith(file, caps, DEFAULT_DEPS, signal);
}

// ── schopnosti prohlížeče ─────────────────────────────────────────────────────

/**
 * Nekomprimovaný šedotónový TIFF 1 × 1 (little-endian): hlavička `II*\0`, jedno IFD
 * s devíti tagy baseline a jeden bajt obrazu.
 */
export function tinyTiffBytes(): Uint8Array {
  const SHORT = 3;
  const LONG = 4;
  const entries: Array<[tag: number, type: number, value: number]> = [
    [256, SHORT, 1], // ImageWidth
    [257, SHORT, 1], // ImageLength
    [258, SHORT, 8], // BitsPerSample
    [259, SHORT, 1], // Compression: žádná
    [262, SHORT, 1], // PhotometricInterpretation: BlackIsZero
    [273, LONG, 0], // StripOffsets (doplní se níže)
    [277, SHORT, 1], // SamplesPerPixel
    [278, SHORT, 1], // RowsPerStrip
    [279, LONG, 1], // StripByteCounts
  ];
  const ifdOffset = 8;
  const dataOffset = ifdOffset + 2 + entries.length * 12 + 4;
  const bytes = new Uint8Array(dataOffset + 1);
  const view = new DataView(bytes.buffer);
  bytes.set([0x49, 0x49, 0x2a, 0x00]);
  view.setUint32(4, ifdOffset, true);
  view.setUint16(ifdOffset, entries.length, true);
  entries.forEach(([tag, type, value], i) => {
    const e = ifdOffset + 2 + i * 12;
    view.setUint16(e, tag, true);
    view.setUint16(e + 2, type, true);
    view.setUint32(e + 4, 1, true);
    const v = tag === 273 ? dataOffset : value;
    if (type === SHORT) view.setUint16(e + 8, v, true);
    else view.setUint32(e + 8, v, true);
  });
  view.setUint32(ifdOffset + 2 + entries.length * 12, 0, true); // žádné další IFD
  bytes[dataOffset] = 0x80;
  return bytes;
}

/**
 * HEIC 16 × 16 (jednobarevný, 502 B) vyrobený na macOS:
 * `sips -s format heic in.png --out out.heic` nad PNG 16 × 16 z Pillow, pak `base64 -i out.heic`.
 */
export const HEIC_SAMPLE_BASE64 =
  "AAAAJGZ0eXBoZWljAAAAAG1pZjFNaVBybWlhZk1pSEJoZWljAAABhm1ldGEAAAAAAAAAIWhkbHIAAAAAAAAAAHBpY3QAAAAA" +
  "AAAAAAAAAAAAAAAAJGRpbmYAAAAcZHJlZgAAAAAAAAABAAAADHVybCAAAAABAAAADnBpdG0AAAAAAAEAAAAjaWluZgAAAAAA" +
  "AQAAABVpbmZlAgAAAAABAABodmMxAAAAAOZpcHJwAAAAxWlwY28AAAATY29scm5jbHgAAgACAAaAAAAADGNsbGkAywBAAAAA" +
  "FGlzcGUAAAAAAAAAEAAAABAAAAAJaXJvdAAAAAAQcGl4aQAAAAADCAgIAAAAcWh2Y0MBA3AAAACwAAAAAAAe8AD8/fj4AAAL" +
  "A6AAAQAXQAEMAf//A3AAAAMAsAAAAwAAAwAecCShAAEAI0IBAQNwAAADALAAAAMAAAMAHqAUIEHAkwziHuRZVNwICBgCogAB" +
  "AAlEAcBhcshAUyQAAAAZaXBtYQAAAAAAAAABAAEGgQIDBYaEAAAAHmlsb2MAAAAARAAAAQABAAAAAQAAAboAAAA8AAAAAW1k" +
  "YXQAAAAAAAAATAAAADgoAa+jVyqh1/5sL0EGll0tEr/5nq/9Ng/+9+aD/5AXfj97++KHOGrL/O/LP0Eu0k6ANfProFSx8A==";

/** Nedoběhnuté dekódování vzorku do této doby = prohlížeč formát neumí. */
const CAPS_PROBE_TIMEOUT_MS = 3000;

type ProbeDecode = (blob: Blob) => Promise<{ close(): void }> | { close(): void };

function probe(decode: ProbeDecode, blob: Blob): Promise<boolean> {
  return new Promise<boolean>((resolve) => {
    let settled = false;
    const finish = (ok: boolean) => {
      if (settled) return;
      settled = true;
      clearTimeout(timer);
      resolve(ok);
    };
    const timer = setTimeout(() => finish(false), CAPS_PROBE_TIMEOUT_MS);
    Promise.resolve()
      .then(() => decode(blob))
      .then(
        (decoded) => {
          quietly(() => decoded.close());
          finish(true);
        },
        () => finish(false),
      );
  });
}

function heicSample(): Blob {
  const bytes = Uint8Array.from(atob(HEIC_SAMPLE_BASE64), (c) => c.charCodeAt(0));
  return new Blob([bytes as BlobPart], { type: "image/heic" });
}

/**
 * Schopnost = naše dekódovací cesta vzorek přečte (ne User-Agent). Bez memoizace (test).
 */
export async function detectBrowserCapsWith(decode: ProbeDecode): Promise<BrowserCaps> {
  const [decodesTif, decodesHeic] = await Promise.all([
    probe(decode, new Blob([tinyTiffBytes() as BlobPart], { type: "image/tiff" })),
    probe(decode, heicSample()),
  ]);
  return { decodesTif, decodesHeic };
}

let capsPromise: Promise<BrowserCaps> | null = null;

/** Co prohlížeč umí dekódovat (TIF, HEIC); jeden sdílený slib, spuštěný napoprvé. */
export function detectBrowserCaps(): Promise<BrowserCaps> {
  capsPromise ??= detectBrowserCapsWith(decodeImage);
  return capsPromise;
}
